import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { ApiService } from '../../js/ApiService';
import RegisterReCaptcha from '../ReCaptcha/RegisterReCaptcha/RegisterReCaptcha';
import ErrorSummary from '../util/ErrorSummary/ErrorSummary';
import SafeHtmlError from '../util/SafeHtmlError/SafeHtmlError';
import Loading from '../util/Loading/Loading';
import useClient from '../util/useClient/useClient';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import JDFNLBrand from '../../styles/themes/status/assets/JD_FNL_Brand.svg';
import InputField from '../util/InputField/InputField';

const schema = z.object({
  firstName: z
    .string()
    .transform(value => value.replaceAll(' ', ''))
    .pipe(z.string().min(1, { message: 'First name is required.' })),
  iusername: z.string().email({ message: 'You must use a valid email.' }),
  ipassword: z
    .string()
    .min(10, { message: 'Password must be at least 10 characters.' })
    .max(25, { message: 'Password must be at most 25 characters.' })
    .regex(/[A-Z]/, { message: 'Password must contain at least 1 uppercase letter.' })
    .regex(/[a-z]/, { message: 'Password must contain at least 1 lowercase letter.' })
    .regex(/[0-9]/, { message: 'Password must contain at least 1 number.' })
    .regex(/[!"#$%'&()*+,\-\.\/:;<=>?@\^_\x60{|}~]/, {
      message: 'Password must contain at least 1 special character.'
    })
    .regex(/^\S+$/, { message: 'Password must not contain spaces.' })
});

// Returned by /users/validate (410) when a POS magic registration link has expired
const MAGIC_REGISTRATION_LINK_EXPIRED = 'MAGIC_REGISTRATION_LINK_EXPIRED';

function FinishCompleteRegistration({ token }) {
  const {
    register,
    errors,
    handleSubmit,
    setValue,
    formState: { isSubmitting, isValid }
  } = useForm({ resolver: zodResolver(schema), mode: 'onChange' });
  const client = useClient();
  const history = useHistory();
  const hiddenForm = useRef(null);
  const [validatingToken, setValidatingToken] = useState(false);
  const [validToken, setValidToken] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [reCaptchaToken, setreCaptchaToken] = useState(null);
  const [readOnlyInputs, setReadOnlyInputs] = useState({
    iusername: false,
    firstName: false
  });
  const [passInputType, setPassInputType] = useState('password');
  const [password, setPassword] = useState('');
  const [passFocused, setpassFocused] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const handleApiResponse = async res => {
    const contentType =
      res.headers && typeof res.headers.get === 'function' && res.headers.get('content-type');
    const isHtml = contentType && contentType.includes('text/html');
    const isJson =
      (contentType && contentType.includes('application/json')) ||
      (!contentType && typeof res.json === 'function');
    let data;
    if (isJson && !isHtml) {
      try {
        // Clone response to avoid locking the original stream if JSON parsing fails
        if (typeof res.clone === 'function') {
          const clonedRes = res.clone();
          try {
            data = await clonedRes.json();
          } catch {
            data = await res.text();
          }
        } else {
          data = await res.json();
        }
      } catch {
        data = await res.text();
      }
    } else {
      data = await res.text();
    }
    if (res.status === 200) return data;
    setErrMsg(data?.error || data || 'An error occurred');
    return null;
  };

  const updateField = useCallback(
    (name, value, isEmail = false) => {
      if (!value?.trim()) return;
      const finalValue = isEmail ? value.toLowerCase() : value;
      setValue(name, finalValue, { shouldValidate: true });
      setReadOnlyInputs(prev => ({ ...prev, [name]: true }));
    },
    [setValue]
  );

  useEffect(() => {
    document.title = `Create Account | ${client.startsWith('finl') ? 'Finish Line' : 'JD Sports'}`;
    const loadUserAndSetup = async () => {
      setValidatingToken(true);
      try {
        const res = await ApiService.validateOTP(token);
        setValidatingToken(false);
        if (res.status === 410) {
          // Read a clone so handleApiResponse can still read the body for any other 410
          const data = await res
            .clone()
            .json()
            .catch(() => null);
          if (data?.error === MAGIC_REGISTRATION_LINK_EXPIRED) {
            const params = new URLSearchParams({ email: data.email || '', type: 'registration' });
            history.push(`/linkExpired?${params.toString()}`, { registrationToken: token });
            return;
          }
        }
        const userInfo = await handleApiResponse(res);
        if (userInfo) {
          setValidToken(true);
          updateField('iusername', userInfo.email || userInfo.username, true);
          updateField('firstName', userInfo.firstName);
        }
      } catch {
        setValidatingToken(false);
        setErrMsg('An error occurred');
      }
    };
    loadUserAndSetup();
  }, [setValue, token, client, updateField, history]);

  useEffect(() => {
    const handleToken = ev => {
      setreCaptchaToken(ev.token);
      setErrMsg(null);
    };
    document.addEventListener('rTokenRdy', handleToken, false);
    return () => {
      document.removeEventListener('rTokenRdy', handleToken, false);
    };
  }, []);

  const onSubmit = async data => {
    if (!reCaptchaToken) return setErrMsg('You need to complete reCaptcha');
    const structuredData = {
      username: data.iusername,
      password: data.ipassword,
      recaptchaToken: reCaptchaToken,
      token,
      userInfo: { clientId: client }
    };
    try {
      const res = await ApiService.completeRegistrationAcc(structuredData);
      if (res.status === 200) {
        setIsSuccessful(true);
        ['username', 'password', 'recaptchaToken'].forEach(key => {
          const val =
            key === 'username'
              ? data.iusername
              : key === 'password'
                ? data.ipassword
                : reCaptchaToken;
          setValue(key, val);
        });
        hiddenForm.current?.submit();
      } else {
        await handleApiResponse(res);
      }
    } catch {
      setErrMsg('An unexpected error occurred during registration.');
    }
  };

  const getLegalLink = type => {
    const isFinl = client === 'finl-web';
    const links = {
      terms: isFinl
        ? 'https://www.finishline.com/status_terms'
        : 'https://www.jdsports.com/status_terms',
      privacy: 'https://www.finishline.com/store/corporate/privacyPolicy.jsp',
      use: isFinl
        ? 'https://www.finishline.com/terms-of-use'
        : 'https://www.jdsports.com/terms-of-use'
    };
    return links[type];
  };

  const showPassword = () => setPassInputType(prev => (prev === 'password' ? 'text' : 'password'));
  const handlePassChange = e => setPassword(e.target.value);
  const showErrorSummary = () => setpassFocused(true);
  const hideErrorSummary = () => setpassFocused(false);

  return (
    <div className="body bg-white" style={{ height: '100%' }} data-testid="finish-complete-reg">
      <div className="d-flex flex-column align-items-center justify-content-center">
        <div className="mt-3">
          <span className="font-weight-bold line-height-1">
            ONE ACCOUNT.
            <br />
            MORE ACCESS.
          </span>
        </div>
        <div className="mt-1">
          <img className="family-logo" src={JDFNLBrand} alt="JD FNL Brand logo" />
        </div>
        <h1 className="h1 mb-4 text-center" style={{ marginTop: '28px' }}>
          COMPLETE STATUS ACCOUNT REGISTRATION
        </h1>
        <div className="text-center mb-3" style={{ lineHeight: '20px' }}>
          <span>
            Please create a password to finish setting up your account and claim your $10 Welcome
            Reward
          </span>
        </div>
      </div>
      {validatingToken && (
        <div className="d-flex align-items-center justify-content-center mb-3">
          <span>Validating Token...</span> <Loading inline />
          {errMsg && <div className="text-danger text-center pb-3">{errMsg}</div>}
        </div>
      )}
      {!validatingToken && !validToken && (
        <div className="text-danger text-center pb-3">{errMsg}</div>
      )}
      {!validatingToken && validToken && (
        <div className="form">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3">
              <InputField
                name="firstName"
                className="form-input"
                type="text"
                placeholder="First Name"
                aria-label="First Name"
                readOnly={readOnlyInputs.firstName}
                ref={register()}
              />
              {errors.firstName && (
                <div className="form-text text-danger" role="alert">
                  {errors.firstName.message}
                </div>
              )}
            </div>

            <div className="mb-3">
              <InputField
                name="iusername"
                className="form-input"
                type="email"
                placeholder="Email"
                aria-label="Email"
                readOnly={readOnlyInputs.iusername}
                onChange={e => (e.target.value = e.target.value.toLowerCase())}
                ref={register()}
              />
              {errors.iusername && (
                <div className="form-text text-danger" role="alert">
                  {errors.iusername.message}
                </div>
              )}
            </div>

            <div className="mb-3">
              <InputField
                name="ipassword"
                type={passInputType}
                className="form-input"
                placeholder="Create Password"
                aria-label="Create Password"
                ref={register()}
                onChange={handlePassChange}
                onFocus={showErrorSummary}
                onBlur={hideErrorSummary}
              />
              <button
                type="button"
                onClick={showPassword}
                className="show-pass"
                style={{ height: 'unset', bottom: '30px' }}
              >
                {passInputType === 'password' ? 'Show' : 'Hide'}
              </button>
              {errors.ipassword && (
                <div className="form-text text-danger" role="alert">
                  {errors.ipassword.message}
                </div>
              )}
            </div>

            <ErrorSummary value={password} show={passFocused} />

            <div className="mb-3">
              <RegisterReCaptcha action="REGISTER" />
            </div>

            <div className="text-center my-3">
              <input
                disabled={isSubmitting || isSuccessful || !isValid}
                type="submit"
                className="btn btn-primary btn-block"
                value="Create Account"
              />
            </div>
            <div className="text-center pb-4" style={{ width: '343px', margin: '0 auto' }}>
              <div>
                <span className="small-font">
                  By creating an account, you enroll in the STATUS Program subject to our&nbsp;
                  <a className="link" href={getLegalLink('terms')}>
                    STATUS Terms &amp; Conditions
                  </a>
                  ,&nbsp;
                  <a className="link" href={getLegalLink('privacy')}>
                    Privacy Policy
                  </a>
                  , and&nbsp;
                  <a className="link" href={getLegalLink('use')}>
                    Terms of Use
                  </a>{' '}
                  and certify that you are 18 years old and older. Ages 13 - 17 must enroll with
                  consent from a parent or guardian who has agreed to be bound by these Terms on
                  your behalf. Children under 13 may not enroll.
                </span>
              </div>
            </div>
            <SafeHtmlError message={errMsg} className="text-danger text-center pb-3" />
          </form>
        </div>
      )}
      <form
        ref={hiddenForm}
        id="hiddenLoginForm"
        className="invisible"
        method="POST"
        action="/account/login"
      >
        <input id="username" name="username" ref={register} type="text" />
        <input id="password" name="password" ref={register} type="password" />
        <input id="recaptchaToken" name="recaptchaToken" ref={register} type="recaptchaToken" />
      </form>
    </div>
  );
}

export default FinishCompleteRegistration;
